import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { HelpCircle, RefreshCw, ThumbsUp, ThumbsDown, MessageSquareText, Pencil, RotateCcw, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return "—"; } };
const pctColor = (p) => (p === null ? "var(--yalola-text-muted)" : p < 50 ? "#B91C1C" : p < 75 ? "#B45309" : "#15803D");
const MUTED = { color: "var(--yalola-text-muted)" };

const Stat = ({ label, value, testId, icon }) => (
  <div className="rounded-xl px-4 py-3" style={{ background: "var(--yalola-surface-alt)" }} data-testid={testId}>
    <div className="flex items-center gap-1.5 text-xs" style={MUTED}>{icon}{label}</div>
    <div className="text-xl font-bold font-display">{value}</div>
  </div>
);

const ResetButton = ({ it, onReset }) => {
  const [arm, setArm] = useState(false);
  const rid = `${it.section}-${it.item}`;
  if (!arm) return <button type="button" onClick={() => setArm(true)} className="p-1.5 rounded-lg hover:bg-black/5" title="Remettre les votes à zéro" data-testid={`faq-votes-reset-${rid}`}><RotateCcw size={14} style={MUTED} /></button>;
  return (
    <span className="inline-flex items-center gap-1">
      <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={() => { setArm(false); onReset(it); }} data-testid={`faq-votes-reset-confirm-${rid}`}>Confirmer la remise à zéro</Button>
      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setArm(false)} data-testid={`faq-votes-reset-cancel-${rid}`}>Annuler</Button>
    </span>
  );
};

const Row = ({ it, isSuper, onReset }) => {
  const [open, setOpen] = useState(false);
  const rid = `${it.section}-${it.item}`;
  return (
    <div className="rounded-xl border" style={{ borderColor: "var(--yalola-border)", borderLeft: `3px solid ${pctColor(it.percent)}` }}
      data-testid={`faq-votes-row-${rid}`} data-percent={it.percent ?? ""} data-total={it.total}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-3 py-2.5">
        <div className="flex-1 min-w-[220px]">
          <div className="text-sm font-semibold" data-testid={`faq-votes-question-${rid}`}>{it.question}</div>
          <div className="text-xs" style={MUTED}>{it.section_title} · <code>{it.key}</code>{it.last_at ? ` · dernier vote ${fmtDate(it.last_at)}` : ""}</div>
        </div>
        <span className="inline-flex items-center gap-1 text-sm font-semibold" style={{ color: "#15803D" }} data-testid={`faq-votes-up-${rid}`}><ThumbsUp size={14} />{it.up}</span>
        <span className="inline-flex items-center gap-1 text-sm font-semibold" style={{ color: "#B91C1C" }} data-testid={`faq-votes-down-${rid}`}><ThumbsDown size={14} />{it.down}</span>
        <div className="w-28">
          <div className="text-xs font-bold text-right" style={{ color: pctColor(it.percent) }} data-testid={`faq-votes-pct-${rid}`}>{it.percent === null ? "Aucun vote" : `${it.percent} % utile`}</div>
          <div className="h-1.5 rounded-full overflow-hidden bg-black/10"><div className="h-full rounded-full" style={{ width: `${it.percent ?? 0}%`, background: pctColor(it.percent) }} /></div>
        </div>
        <button type="button" onClick={() => setOpen((o) => !o)} disabled={!it.comments_count} aria-expanded={open}
          className="inline-flex items-center gap-1 text-xs rounded-full border px-2.5 py-1 disabled:opacity-40" style={{ borderColor: "var(--yalola-border)" }}
          title="Remarques laissées après un 👎" data-testid={`faq-votes-comments-toggle-${rid}`}><MessageSquareText size={13} />{it.comments_count}</button>
        <Link to={`/admin?tab=i18n&q=${encodeURIComponent(it.key)}`} className="inline-flex items-center gap-1 text-xs font-semibold hover:underline" style={{ color: "var(--yalola-primary)" }} data-testid={`faq-votes-edit-${rid}`}><Pencil size={13} />Modifier le texte</Link>
        {isSuper && it.total > 0 && <ResetButton it={it} onReset={onReset} />}
      </div>
      {open && (
        <ul className="px-3 pb-3 space-y-1.5" data-testid={`faq-votes-comments-${rid}`}>
          {it.comments.map((c, i) => (
            <li key={i} className="text-xs rounded-lg px-3 py-2" style={{ background: "var(--yalola-surface-alt)" }} data-testid={`faq-votes-comment-${rid}-${i}`}>
              <span className="uppercase font-bold mr-2 opacity-60">{c.lang || "fr"}</span>{c.text}<span className="ml-2 opacity-60">{fmtDate(c.at)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// Admin › Supervision › FAQ — retours : 👍 / 👎 / % utile par question, moins utiles d'abord, remarques anonymes, lien vers la clé faq.*.
export const FaqVotesPanel = ({ isSuper }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/admin/faq/votes"); setData(r.data); } catch { toast.error("Chargement impossible"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const reset = async (it) => {
    try { await api.delete(`/admin/faq/votes/${it.section}/${it.item}`); toast.success("Votes remis à zéro"); load(); } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
  };
  const t = data?.totals;
  return (
    <div className="space-y-3" data-testid="admin-faq-votes-panel">
      <div className="yalola-card p-4 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 font-bold font-display text-lg"><HelpCircle size={18} />FAQ — retours des lecteurs</div>
            <p className="text-sm mt-1" style={MUTED}>Chaque réponse de /faq propose « Cette réponse vous a-t-elle aidé ? ». Les questions les moins utiles apparaissent en premier ; les remarques laissées après un 👎 sont anonymes (un vote par appareil, modifiable).</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {data && (
              <Link to="/admin?tab=settings&section=identity&focus=feat-faq_votes" className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold"
                style={{ background: data.enabled ? "#DCFCE7" : "#FEE2E2", color: data.enabled ? "#15803D" : "#B91C1C" }} title="Interrupteur : Paramètres › Modules activés"
                data-testid="faq-votes-toggle-link" data-enabled={data.enabled ? "1" : "0"}><Settings2 size={13} />{data.enabled ? "Votes activés" : "Votes désactivés"}</Link>
            )}
            <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="faq-votes-refresh"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /></Button>
          </div>
        </div>
        {t && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Stat label="Utile" value={t.up} icon={<ThumbsUp size={12} />} testId="faq-votes-total-up" />
            <Stat label="Pas utile" value={t.down} icon={<ThumbsDown size={12} />} testId="faq-votes-total-down" />
            <Stat label="Remarques" value={t.comments} icon={<MessageSquareText size={12} />} testId="faq-votes-total-comments" />
            <Stat label="Questions votées" value={`${t.voted_questions} / ${t.questions}`} icon={<HelpCircle size={12} />} testId="faq-votes-total-voted" />
          </div>
        )}
      </div>
      {!data && <div className="yalola-card p-6 animate-pulse h-40" data-testid="faq-votes-loading" />}
      {data && <div className="space-y-2" data-testid="faq-votes-list">{data.items.map((it) => <Row key={`${it.section}-${it.item}`} it={it} isSuper={isSuper} onReset={reset} />)}</div>}
    </div>
  );
};
